"use client";

import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Gauge,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DemoReportDetailsModal } from "@/components/landing/demo-report-details-modal";
import { cn } from "@/lib/utils";
import type { DemoPreview } from "@/src/features/demo-contracts/types";

/* ─── Validation badge ─────────────────────────────────────────────────── */

function validationBadgeStyles(
  level: NonNullable<DemoPreview["validationLevel"]>,
): {
  label: string;
  className: string;
  icon: typeof CheckCircle2;
} {
  if (level === "CONFIDENT") {
    return {
      label: "Confident",
      className: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
      icon: CheckCircle2,
    };
  }

  if (level === "MVP") {
    return {
      label: "MVP",
      className: "border-sky-400/30 bg-sky-400/10 text-sky-300",
      icon: Gauge,
    };
  }

  if (level === "INSUFFICIENT") {
    return {
      label: "Insufficient",
      className: "border-amber-400/30 bg-amber-400/10 text-amber-300",
      icon: AlertTriangle,
    };
  }

  return {
    label: "Abort",
    className: "border-rose-400/30 bg-rose-400/10 text-rose-300",
    icon: XCircle,
  };
}

function formatConsistencyScore(score: number): string {
  const normalizedScore = score > 1 ? score / 100 : score;
  const clampedScore = Math.min(1, Math.max(0, normalizedScore));
  return `${Math.round(clampedScore * 100)}%`;
}

/* ─── Preview header ────────────────────────────────────────────────────── */

function PreviewHeader({
  nicheSlug,
  kind,
  postsCollected,
  validationLevel,
  consistencyScore,
  surface,
}: Pick<
  DemoPreview,
  | "nicheSlug"
  | "kind"
  | "postsCollected"
  | "validationLevel"
  | "consistencyScore"
> & { surface: "standalone" | "embedded" }) {
  const validationMeta =
    validationLevel != null ? validationBadgeStyles(validationLevel) : null;
  const ValidationIcon = validationMeta?.icon;

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        {/* Kind label — monospace */}
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/30">
          {kind === "sample" ? "Sample Report" : "Live Report"}
        </p>
        {/* Niche name — clear hierarchy */}
        <p className="mt-1 font-serif text-[19px] font-semibold leading-tight text-primary-foreground sm:text-[20px]">
          {nicheSlug
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase())}
        </p>
      </div>

      {/* Metadata badges — monospace accent */}
      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        {validationMeta && (
          <Badge
            variant="outline"
            className={cn(
              "font-mono text-[10px] uppercase tracking-widest sm:text-[11px]",
              validationMeta.className,
            )}
          >
            {ValidationIcon && (
              <ValidationIcon className="size-3" aria-hidden="true" />
            )}
            {validationMeta.label}
          </Badge>
        )}
        {surface === "standalone" && consistencyScore != null && (
          <Badge
            variant="outline"
            className="font-mono border-white/15 bg-white/5 text-[10px] uppercase tracking-widest text-primary-foreground/58 sm:text-[11px]"
          >
            <Gauge className="size-3" aria-hidden="true" />
            {formatConsistencyScore(consistencyScore)} consistency
          </Badge>
        )}
        {postsCollected != null && postsCollected > 0 && (
          <Badge
            variant="secondary"
            className="font-mono border-white/10 bg-white/[0.04] text-[10px] uppercase tracking-widest text-primary-foreground/58 sm:text-[11px]"
          >
            <BarChart3 className="size-3" aria-hidden="true" />
            {postsCollected.toLocaleString()} posts
          </Badge>
        )}
      </div>
    </div>
  );
}

/* ─── Pain signal block ─────────────────────────────────────────────────── */

function PainSignalBlock({
  description,
  onSeeDetails,
}: {
  description: string;
  onSeeDetails: () => void;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/30">
        Strongest signal in this preview
      </p>
      <p className="mt-1 font-mono text-[12px] leading-[1.75] text-primary-foreground/80 sm:text-[13px]">
        {description}
      </p>
      <button
        type="button"
        className="mt-3 font-mono text-[10px] uppercase tracking-widest text-accent/70 transition-colors hover:text-accent"
        onClick={onSeeDetails}
      >
        Open the full report →
      </button>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */

export interface DemoPreviewCardProps {
  preview: DemoPreview;
  surface?: "standalone" | "embedded";
  className?: string;
}

export function DemoPreviewCard({
  preview,
  surface = "standalone",
  className,
}: DemoPreviewCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const {
    nicheSlug,
    kind,
    postsCollected,
    validationLevel,
    consistencyScore,
    summary,
    painPoints,
    confidenceDisclaimer,
  } = preview;

  return (
    <div
      className={cn(
        "w-full space-y-4 rounded-xl border p-5 sm:space-y-5",
        surface === "standalone"
          ? "border-white/15 bg-white/[0.04]"
          : "border-white/10 bg-white/[0.02]",
        className,
      )}
      role="region"
      aria-label="Demo report preview"
    >
      <PreviewHeader
        nicheSlug={nicheSlug}
        kind={kind}
        postsCollected={postsCollected}
        validationLevel={validationLevel}
        consistencyScore={consistencyScore}
        surface={surface}
      />

      {/* Summary */}
      <p
        className={cn(
          "font-mono text-[13px] leading-[1.8] text-primary-foreground/72 sm:text-[14px]",
          surface === "embedded" && "line-clamp-2",
        )}
      >
        {summary}
      </p>

      {/* Pain signals — embedded surface */}
      {surface === "embedded" && painPoints.length > 0 && (
        <PainSignalBlock
          description={painPoints[0].description}
          onSeeDetails={() => setDetailsOpen(true)}
        />
      )}

      {/* Pain signals teaser — standalone surface */}
      {surface === "standalone" && painPoints.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/30">
            {painPoints.length} ranked pain signals ready for review
          </p>
          <p className="mt-1 font-mono text-[12px] leading-[1.7] text-primary-foreground/68 sm:text-[13px]">
            Open the report to review citations, urgency, customer patterns, and
            validation notes in one place.
          </p>
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-wrap items-center gap-3 pt-0.5">
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-accent/20 bg-accent/10 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-accent transition-colors hover:border-accent/30 hover:bg-accent/15"
          onClick={() => setDetailsOpen(true)}
          aria-haspopup="dialog"
        >
          Review full report
          <ArrowRight className="size-3" aria-hidden="true" />
        </button>
      </div>

      <DemoReportDetailsModal
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        preview={preview}
      />

      {/* Confidence disclaimer */}
      {confidenceDisclaimer && (
        <p className="border-t border-white/5 pt-3 font-mono text-[11px] leading-[1.7] text-primary-foreground/48">
          {confidenceDisclaimer}
        </p>
      )}
    </div>
  );
}
